import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PrintIcon from '@mui/icons-material/Print';
import CircularProgress from '@mui/material/CircularProgress';
import '../Customer/Customer.css';

function RentDetails() {
    const [dataItem, setDataItem] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const { isAuthenticated, companyId, permissions, token, logout } = useAuth();
    const [totalrent, setRent] = useState(0);
    const [totalpaid, setPaid] = useState(0);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        setFromDate(getFormattedStartDate());
        setToDate(getFormattedCurrentDate());
    }, [isAuthenticated, companyId, token, logout]);

    useEffect(() => {
        if (fromDate && toDate) {
            fetchTableData();
        }
    }, [fromDate, toDate]);

    useEffect(() => {
        let totalrent = 0;
        let totalpaid = 0;
        dataItem.forEach(item => {
            totalrent += item.RentAmount;
            totalpaid += item.PaidAmount;
        });
        setRent(totalrent);
        setPaid(totalpaid);
    }, [dataItem]);

    const getFormattedCurrentDate = () => {
        const currentDate = new Date();
        return currentDate.toISOString().split('T')[0];
    };

    const getFormattedStartDate = () => {
        const monthStartDate = new Date();
        monthStartDate.setDate(1);
        return monthStartDate.toISOString().split('T')[0];
    };

    const hasReadReportPermission = permissions.includes('Read Reports');

    const fetchTableData = async () => {
        if (hasReadReportPermission) {
            setDataItem([]);
            setLoading(true);
            try {
                const response = await fetchData('ReportApi', `/RentAmountDetails/${companyId}/0/${fromDate}/${toDate}`, token);
                if (response.responseCode === '1111') {
                    setDataItem(response.response_data);
                }
            } catch (error) {
                console.error('Error fetching Rent Details:', error);
            } finally {
                setLoading(false);
            }
        }
    };

    const filteredDataItem = dataItem.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const formatDate = (dateString) => {
        if (!dateString) return '—';
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = date.toLocaleString('default', { month: 'short' });
        const year = date.getFullYear();
        return `${day} ${month} ${year}`;
    };

    const handleExportExcel = () => {
        const excelData = [];
        const headerCaption = [`Rent Report From : ${formatDate(fromDate)} To : ${formatDate(toDate)}`];
        excelData.push(headerCaption);
        const headerRow = ['Sl.No', 'Bill No', 'Bill Date', 'Customer Name', 'Contact', 'Room', 'Rent', 'Paid'];
        excelData.push(headerRow);

        filteredDataItem.forEach((rowData, index) => {
            const dataRow = [
                index + 1,
                rowData.BillNo,
                formatDate(rowData.BillDate),
                rowData.CustomerName,
                `${rowData.ContactNumber1}${rowData.ContactNumber2 ? ', ' + rowData.ContactNumber2 : ''}`,
                rowData.RoomName,
                rowData.RentAmount,
                rowData.PaidAmount
            ];
            excelData.push(dataRow);
        });

        const totalRow = ['', '', '', '', '', 'Total:', totalrent, totalpaid];
        excelData.push(totalRow);

        const ws = XLSX.utils.aoa_to_sheet(excelData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Rent Report');
        ws['!cols'] = [{ wch: 8 }, { wch: 15 }, { wch: 15 }, { wch: 25 }, { wch: 30 }, { wch: 15 }, { wch: 12 }, { wch: 12 }];
        XLSX.writeFile(wb, 'Rent Report.xlsx');
    };

    const handlePrint = () => {
        const printWindow = window.open('', '_blank');
        if (!printWindow) return;

        printWindow.document.write(`
            <html>
            <head>
                <title>Rent Report</title>
                <style>
                    body { font-family: Arial, sans-serif; padding: 20px; }
                    h2 { color: #1e3a5f; margin-bottom: 20px; }
                    table { border-collapse: collapse; width: 100%; }
                    th { background: #1e3a5f; color: white; padding: 10px; text-align: left; font-size: 12px; }
                    td { border: 1px solid #e2e8f0; padding: 8px; font-size: 12px; }
                    tr:nth-child(even) { background: #f8fafc; }
                    .paid-row { background: #d1fae5 !important; }
                    .amount { text-align: right; }
                    .total-row { background: #1e3a5f !important; color: white; font-weight: bold; }
                </style>
            </head>
            <body>
                <h2>Rent Details Report</h2>
                <p>From: ${formatDate(fromDate)} To: ${formatDate(toDate)}</p>
                <table>
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Bill No</th>
                            <th>Bill Date</th>
                            <th>Customer</th>
                            <th>Contact</th>
                            <th>Room</th>
                            <th class="amount">Rent</th>
                            <th class="amount">Paid</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${filteredDataItem.map((item, index) => `
                            <tr class="${item.RentAmount === item.PaidAmount ? 'paid-row' : ''}">
                                <td>${index + 1}</td>
                                <td>${item.BillNo}</td>
                                <td>${formatDate(item.BillDate)}</td>
                                <td>${item.CustomerName}</td>
                                <td>${item.ContactNumber1}${item.ContactNumber2 ? ', ' + item.ContactNumber2 : ''}</td>
                                <td>${item.RoomName}</td>
                                <td class="amount">₹${item.RentAmount.toFixed(2)}</td>
                                <td class="amount">₹${item.PaidAmount.toFixed(2)}</td>
                            </tr>
                        `).join('')}
                        <tr class="total-row">
                            <td colspan="6" style="text-align: right;">Total:</td>
                            <td class="amount">₹${totalrent.toFixed(2)}</td>
                            <td class="amount">₹${totalpaid.toFixed(2)}</td>
                        </tr>
                    </tbody>
                </table>
            </body>
            </html>
        `);
        printWindow.document.close();
        printWindow.print();
    };

    return (
        <div className="customer-container">
            {loading && (
                <div className="loading-overlay">
                    <CircularProgress size={40} className="loading-spinner" />
                    <span className="loading-text">Loading...</span>
                </div>
            )}

            {/* Header */}
            <div className="page-header" style={{ flexWrap: 'wrap' }}>
                <h1 className="page-title">Rent Details</h1>
                <div className="header-actions" style={{ flexWrap: 'wrap', gap: '10px' }}>
                    <div className="search-box">
                        <SearchIcon />
                        <input
                            type="text"
                            className="search-input"
                            placeholder="Search..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            <label style={{ fontSize: '11px', color: '#64748b', fontWeight: '500' }}>From</label>
                            <input
                                className="form-input"
                                type="date"
                                value={fromDate}
                                onChange={(e) => setFromDate(e.target.value)}
                                style={{ width: '130px', padding: '6px 8px', fontSize: '13px' }}
                            />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            <label style={{ fontSize: '11px', color: '#64748b', fontWeight: '500' }}>To</label>
                            <input
                                className="form-input"
                                type="date"
                                value={toDate}
                                onChange={(e) => setToDate(e.target.value)}
                                style={{ width: '130px', padding: '6px 8px', fontSize: '13px' }}
                            />
                        </div>
                    </div>
                    <button
                        onClick={fetchTableData}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '8px 14px',
                            background: '#1e3a5f',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '500'
                        }}
                    >
                        <RefreshIcon fontSize="small" />
                        Refresh
                    </button>
                    <button
                        onClick={handleExportExcel}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '8px 14px',
                            background: '#059669',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '500'
                        }}
                    >
                        <FileDownloadIcon fontSize="small" />
                        Excel
                    </button>
                    <button
                        onClick={handlePrint}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '8px 14px',
                            background: '#7c3aed',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '500'
                        }}
                    >
                        <PrintIcon fontSize="small" />
                        Print
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="table-container">
                <div className="table-wrapper">
                    <table className="customer-table">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Bill No</th>
                                <th>Bill Date</th>
                                <th>Customer</th>
                                <th>Contact</th>
                                <th>Room</th>
                                <th style={{ textAlign: 'right' }}>Rent</th>
                                <th style={{ textAlign: 'right' }}>Paid</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredDataItem.length > 0 ? (
                                <>
                                    {filteredDataItem.map((item, index) => (
                                        <tr key={index} style={{ background: item.RentAmount === item.PaidAmount ? '#d1fae5' : '' }}>
                                            <td>{index + 1}</td>
                                            <td className="cell-primary">{item.BillNo}</td>
                                            <td className="cell-date">{formatDate(item.BillDate)}</td>
                                            <td className="cell-name">{item.CustomerName}</td>
                                            <td>{item.ContactNumber1}{item.ContactNumber2 ? `, ${item.ContactNumber2}` : ''}</td>
                                            <td><span className="cell-room">{item.RoomName}</span></td>
                                            <td style={{ textAlign: 'right', fontWeight: '600' }}>₹ {item.RentAmount.toFixed(2)}</td>
                                            <td style={{ textAlign: 'right', fontWeight: '600', color: item.RentAmount === item.PaidAmount ? '#059669' : '#dc2626' }}>
                                                ₹ {item.PaidAmount.toFixed(2)}
                                            </td>
                                        </tr>
                                    ))}
                                    <tr style={{ background: '#1e3a5f' }}>
                                        <td colSpan="6" style={{ textAlign: 'right', color: '#fff', fontWeight: '600' }}>Total:</td>
                                        <td style={{ textAlign: 'right', color: '#fff', fontWeight: '600' }}>₹ {totalrent.toFixed(2)}</td>
                                        <td style={{ textAlign: 'right', color: '#fff', fontWeight: '600' }}>₹ {totalpaid.toFixed(2)}</td>
                                    </tr>
                                </>
                            ) : (
                                <tr>
                                    <td colSpan="8">
                                        <div className="empty-state">
                                            <p>No rent details found</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

export default RentDetails;
